import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type {
  OrganizationPrepayment,
  PrepaymentTransaction
} from "@packages/client";

export const getOrganizationPrepayment = createAsyncThunk(
  "app/organization/prepayment",
  async ({ organizationId }: { organizationId: string }) => {
    const platformLib = await import("Libs/platform");
    const { entities } = platformLib;

    const prepayment = await entities.OrganizationPrepayment.get({
      organizationId
    });

    return prepayment;
  }
);

export const getPrepaymentTransactions = createAsyncThunk(
  "app/organization/prepayment_transactions",
  async ({ organizationId }: { organizationId: string }) => {
    const platformLib = await import("Libs/platform");
    const { entities } = platformLib;

    const transactions = await entities.OrganizationPrepayment.getTransactions({
      organizationId
    });

    return transactions;
  }
);

export type PrepaymentState = Readonly<{
  data: {
    [organizationId: string]: OrganizationPrepayment | undefined;
  };
  transactions: {
    [organizationId: string]: PrepaymentTransaction[] | undefined;
  };
  loading: boolean;
  transactionsLoading: boolean;
  errors?: unknown;
}>;

const initialState: PrepaymentState = {
  data: {},
  transactions: {},
  loading: false,
  transactionsLoading: false
};

const organizationPrepayment = createSlice({
  name: "organizationPrepayment",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOrganizationPrepayment.pending, state => {
        state.loading = true;
      })
      .addCase(
        getOrganizationPrepayment.fulfilled,
        (state, { meta, payload }) => {
          const organizationId = meta.arg.organizationId;
          state.data[organizationId] = payload;
          state.loading = false;
          state.errors = undefined;
        }
      )
      .addCase(getOrganizationPrepayment.rejected, (state, { payload }) => {
        state.loading = false;
        state.errors = payload;
      })
      .addCase(getPrepaymentTransactions.pending, state => {
        state.transactionsLoading = true;
      })
      .addCase(
        getPrepaymentTransactions.fulfilled,
        (state, { meta, payload }) => {
          const organizationId = meta.arg.organizationId;
          state.transactions[organizationId] = [...payload].reverse();
          state.transactionsLoading = false;
          state.errors = undefined;
        }
      )
      .addCase(getPrepaymentTransactions.rejected, (state, { payload }) => {
        state.transactionsLoading = false;
        state.errors = payload;
      });
  }
});

export default organizationPrepayment.reducer;
